// CPAYMENT-1786: the single implementation of "send the receipt to the BFF".
//
// Before this the call was copy-pasted into five screens (UploadPage, LionUploadPage,
// PincoUploadPage, OnePageUploadPage, OnePagePaymentPage), each with its own `catch` that reduced
// every possible failure to one string on screen and reported nothing anywhere. That is why the
// production incident could not be explained: there was no record of what actually happened.
//
// What lives here: attempt classification, the analytics events, the boot beacon, the Sentry
// capture, the retry policy and the 409-is-success rule. Screens keep only their own UI.

import axios from 'axios';
import * as Sentry from '@sentry/react';
import { track } from '../analytics/tracker';
import { beacon } from '../analytics/beacon';
import { buildBasePayload } from '../analytics/buildBasePayload';
import {
  AnalyticsBasePayload,
  ReceiptConnectionPayload,
  ReceiptFilePayload,
  ReceiptRejectionReason,
  ReceiptUploadFailureReason,
} from '../analytics/events';
import { getReceiptFormatErrorKey, validateReceiptFile } from './receiptUploadPolicy';
import { isVideoFile } from './conciergeVideo';
import { Transaction } from './types';

/**
 * No client-side timeout, deliberately.
 *
 * `XMLHttpRequest.timeout` — which is what axios `timeout` becomes in a browser — covers the whole
 * request *including sending the body*. The BFF's own 120s starts only once it has the complete
 * body, so the two do not compose: the client's budget is upload time + backend time. Any fixed
 * value low enough to be useful aborts a slow upload that would have succeeded, and this ticket
 * exists because of false upload errors on exactly those connections — a 5 MB receipt on a 200 kbps
 * uplink needs well over three minutes of upload alone.
 *
 * The request is bounded from the outside instead: the BFF answers after 120s, and the ingress
 * read timeout sits above that. Both produce a real response the page can classify.
 */

// Only a request that never got an answer is retried. A server response — any status — is a
// decision, and repeating it just multiplies load and confuses the client.
const RETRYABLE: ReceiptUploadFailureReason[] = ['offline', 'network'];
const RETRY_DELAYS_MS = [2000, 4000, 8000];

export type ReceiptUploadResult =
  | { ok: true; alreadyUploaded: boolean; attempts: number }
  | {
      ok: false;
      reason: ReceiptUploadFailureReason;
      httpStatus: number | null;
      attempts: number;
    };

export interface ReceiptUploadHandlers {
  /** 0..100 for the current attempt. */
  onProgress?: (percent: number) => void;
  /** A retry is scheduled: let the screen say so instead of showing a bare error. */
  onRetryScheduled?: (attempt: number, delayMs: number) => void;
}

export interface ReceiptUploadOptions extends ReceiptUploadHandlers {
  /**
   * A replacement for a receipt the auto-check rejected, not a first upload.
   *
   * This decides how 409 is read, and the two readings are opposite. On a first upload 409 means
   * the file is already stored — the aftermath of an attempt that timed out on the BFF while the
   * backend finished writing — so the client should move on. On a replacement it means the backend
   * refused to replace anything (the ticket left `FILES_AUTO_VERIFICATION`, usually because the
   * poll moved it to the operator queue while the client was picking a file): the corrected receipt
   * was NOT stored, and reporting success would tell the client his fix was accepted while the
   * operator still sees the rejected original.
   */
  isReplacement?: boolean;
  /** Abort a retry sequence when the screen goes away or the ticket reaches a final status. */
  signal?: AbortSignal;
  /**
   * Send the receipt and confirm the payment in one request (`/api/confirm-with-receipt`) instead
   * of storing the receipt alone. The backend route is idempotent, so 409 is never read as «already
   * stored»: it would say nothing about the payment.
   */
  confirmPayment?: boolean;
}

const fileExtOf = (name: string): string =>
  name.includes('.') ? (name.split('.').pop() ?? '').toLowerCase() : '';

export const describeFile = (file: File): ReceiptFilePayload => ({
  fileName: file.name,
  fileExt: fileExtOf(file.name),
  fileMime: file.type,
  fileSize: file.size,
});

/**
 * `navigator.connection` is Chromium-only and absent on iOS Safari — hence the nulls rather than
 * defaults. Where it exists (most of our Android traffic) it turns "the client was on a slow mobile
 * network" from a guess into a measurement.
 */
const describeConnection = (): ReceiptConnectionPayload => {
  const connection = (navigator as any)?.connection;
  return {
    online: navigator.onLine !== false,
    effectiveType: connection?.effectiveType ?? null,
    downlink: typeof connection?.downlink === 'number' ? connection.downlink : null,
  };
};

export const classifyUploadError = (error: any): ReceiptUploadFailureReason => {
  // An answer from the server is a decision, whatever the device thinks of its connectivity a
  // moment later. Checking `navigator.onLine` first (as this did) turned a 413 into `offline` when
  // the connection dropped right after the response — contradictory telemetry, and worse, `offline`
  // is retryable, so a file the server had just rejected was sent three more times.
  const status = error?.response?.status;
  if (typeof status === 'number') {
    return status >= 500 ? 'http_5xx' : 'http_4xx';
  }
  if (error?.code === 'ERR_CANCELED' || axios.isCancel?.(error)) return 'aborted';
  if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') return 'timeout';
  // Only a response-less failure can be attributed to the device being offline.
  if (navigator.onLine === false) return 'offline';
  // A request object without a response means it left the code but never came back — the branch
  // that produced no trace at all before this change.
  if (error?.request) return 'network';
  return 'client';
};

const wait = (ms: number, resumeOnOnline: boolean, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    let done = false;
    let timer = 0;
    const finish = () => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      window.removeEventListener('online', finish);
      signal?.removeEventListener('abort', finish);
      resolve();
    };
    // Coming back online is the event we are actually waiting for; the timer is only a ceiling.
    if (resumeOnOnline) window.addEventListener('online', finish);
    signal?.addEventListener('abort', finish);
    timer = window.setTimeout(finish, ms);
  });

const captureFailure = (
  basePayload: AnalyticsBasePayload,
  filePayload: ReceiptFilePayload,
  connection: ReceiptConnectionPayload,
  reason: ReceiptUploadFailureReason,
  httpStatus: number | null,
  durationMs: number,
  attempt: number,
  progressPercent: number,
  error: unknown,
): void => {
  try {
    Sentry.withScope((scope) => {
      // Without an explicit fingerprint this lands in the page-wide catch-all issues (`settle`,
      // `request.onerror`), where thousands of unrelated events already live and no alert on it
      // could mean anything.
      scope.setFingerprint(['receipt.upload_failed', reason, String(httpStatus ?? '')]);
      scope.setLevel('error');
      scope.setTags({
        receipt_upload_reason: reason,
        http_status: String(httpStatus ?? ''),
        uuid: basePayload.uuid ?? '',
        merchant: basePayload.merchant ?? '',
        template: basePayload.template,
        payment_method: basePayload.paymentMethod ?? '',
      });
      scope.setContext('receipt', {
        ...filePayload,
        ...connection,
        attempt,
        durationMs,
        progressPercent,
      });
      Sentry.captureException(
        error instanceof Error ? error : new Error(`receipt.upload_failed:${reason}`),
      );
    });
  } catch {
    // telemetry must never reach the payment flow
  }
};

/**
 * CPAYMENT-1821: everything that happens when the client picks a file, in one place.
 *
 * The screens used to validate the file and keep the `File` object itself until «Я оплатил» was
 * pressed. A `File` is a *handle*, not the bytes: the browser opens the underlying source only when
 * the request body is assembled, which can be minutes later and in another app's lifetime. On
 * Android the source is usually a content provider, and a cloud pick (Drive, «Файлы») is a lazy
 * reference that downloads on read — so by send time it can be gone, moved, rewritten or simply
 * unreachable. The browser then aborts the request without ever opening a socket, and — because XHR
 * does not expose the network error to JS — the page saw a bare `Network Error` and told a client
 * with a perfectly good connection to check his connection. Measured on prod: 89 sessions in five
 * days, 40 receipts lost outright, 38 of those deposits expired.
 *
 * Reading the bytes here closes the window: from this point the upload carries an in-memory copy and
 * no longer depends on the file surviving. And it is also the diagnosis, because the File API *does*
 * name the failure (`NotReadableError`, `NotFoundError`, …) where XHR never could.
 */
export type ReceiptPrepareError = ReceiptRejectionReason | 'unreadable';

export interface ReceiptPrepareResult {
  ok: boolean;
  /** The materialised copy — upload this, never the picked handle. */
  file: File | null;
  error: ReceiptPrepareError | null;
}

/**
 * `Blob.arrayBuffer` is missing on iOS Safari below 14, which is still a live share of the traffic
 * this page serves. Falling back to `FileReader` keeps those clients able to attach a receipt at
 * all — the alternative is turning a browser-support gap into the very failure this change removes.
 */
const readBytes = (file: File): Promise<ArrayBuffer> => {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      reader.result instanceof ArrayBuffer
        ? resolve(reader.result)
        : reject(new Error('FileReaderEmptyResult'));
    reader.onerror = () => reject(reader.error ?? new Error('FileReaderError'));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * The in-memory copy the upload will carry.
 *
 * `new File(...)` is not everywhere: old Android WebViews and Safari below 10 expose the `File`
 * *interface* but no constructor, and it throws. That throw used to escape `prepareReceipt`
 * entirely — the screen stayed busy forever and nothing was reported — which is the same silence a
 * client sees when the picker gives nothing back, and it was indistinguishable from it in the data.
 * A `Blob` carrying the name does everything we need of it (`.name`, `.size`, `.type`, and a
 * multipart part), so the fallback is a real one rather than a nicer error.
 */
const materialise = (bytes: ArrayBuffer, picked: File): File => {
  try {
    return new File([bytes], picked.name, {
      type: picked.type,
      lastModified: picked.lastModified,
    });
  } catch {
    const blob = new Blob([bytes], { type: picked.type }) as any;
    blob.name = picked.name;
    blob.lastModified = picked.lastModified;
    return blob as File;
  }
};

export const prepareReceipt = async (
  // Optional, unlike the upload's: nothing is posted here, and the legacy screen carries `uuid?`.
  uuid: string | undefined,
  transaction: Transaction | null | undefined,
  picked: File,
): Promise<ReceiptPrepareResult> => {
  const basePayload = buildBasePayload(uuid, transaction);
  const filePayload = describeFile(picked);

  const { ok, error } = validateReceiptFile(transaction?.merchant, picked, Boolean(transaction?.concierge));
  if (!ok) {
    const reason = error ?? 'invalidFormat';
    track({
      name: 'receipt.file_rejected',
      payload: { ...basePayload, ...filePayload, reason },
    });
    // Beacon only for the empty pick: the format and size rejections have always been visible and
    // are counted from the events alone, while this one used to arrive as a generic upload error
    // and needs a channel that survives a blocked Sentry tunnel, like the unreadable read below.
    if (reason === 'fileEmpty') beacon('receipt-reject-empty');
    return { ok: false, file: null, error: reason };
  }

  const startedAt = Date.now();
  // A chat video goes as picked: up to 100 MB read into memory, and copied again by the
  // materialisation, is what gets a phone's tab killed.
  if (transaction?.concierge && isVideoFile(picked)) {
    track({
      name: 'receipt.file_selected',
      payload: { ...basePayload, ...filePayload, readMs: 0 },
    });
    return { ok: true, file: picked, error: null };
  }

  let bytes: ArrayBuffer;
  try {
    bytes = await readBytes(picked);
    // A provider that answers with fewer bytes than it promised is as unusable as one that throws:
    // the upload would succeed and the operator would get a truncated PDF. A zero-length pick never
    // reaches this point — the policy rejects it above — so the length can be compared outright.
    if (bytes.byteLength !== picked.size) {
      // Named, because the reason field is what groups these in Sentry — a bare `Error` would land
      // in the same bucket as everything else the read can throw.
      throw Object.assign(new Error(`read ${bytes.byteLength} of ${picked.size} bytes`), {
        name: 'ShortRead',
      });
    }
  } catch (readError: any) {
    const reason: string = readError?.name || readError?.message || 'UnknownReadError';
    const durationMs = Date.now() - startedAt;
    track({
      name: 'receipt.file_unreadable',
      payload: { ...basePayload, ...filePayload, reason, durationMs },
    });
    beacon('receipt-reject-unreadable');
    try {
      Sentry.withScope((scope) => {
        scope.setFingerprint(['receipt.file_unreadable', reason]);
        scope.setLevel('error');
        scope.setTags({
          receipt_read_reason: reason,
          uuid: basePayload.uuid ?? '',
          merchant: basePayload.merchant ?? '',
          template: basePayload.template,
        });
        scope.setContext('receipt', { ...filePayload, durationMs });
        Sentry.captureException(
          readError instanceof Error ? readError : new Error(`receipt.file_unreadable:${reason}`),
        );
      });
    } catch {
      // telemetry must never reach the payment flow
    }
    return { ok: false, file: null, error: 'unreadable' };
  }

  track({
    name: 'receipt.file_selected',
    payload: { ...basePayload, ...filePayload, readMs: Date.now() - startedAt },
  });

  return { ok: true, file: materialise(bytes, picked), error: null };
};

/** Message for a pick that never became a file we can send. */
export const prepareErrorTextKey = (
  error: ReceiptPrepareError | null,
  merchant?: string,
  concierge = false,
  // The picked file: a video in the chat has its own limit, so its message names that one.
  file?: File,
): string => {
  if (error === 'invalidFormat') return getReceiptFormatErrorKey(merchant, concierge);
  if (error === 'fileTooLarge') return concierge && file && isVideoFile(file) ? 'upload.videoTooLarge' : 'upload.fileTooLarge';
  // Sits next to the format and size messages: like them it names the file as the problem and
  // leaves the client one obvious move — pick another one.
  if (error === 'fileEmpty') return 'upload.fileEmpty';
  // Lives under `uploadStatus` next to `offline` / `networkError`: it is the outcome of an attempt,
  // not a static label of the upload block.
  return 'uploadStatus.fileUnreadable';
};

/**
 * CPAYMENT-1786: one text per distinguishable situation. Everything used to collapse into
 * `upload.uploadError` ("Произошла ошибка при загрузке"), including a 413 that had its own message
 * and a 409 that was not an error at all — so the client had no way to know whether to change the
 * file, wait, or stop trying.
 */
export const uploadErrorTextKey = (
  reason: ReceiptUploadFailureReason,
  httpStatus: number | null,
): string => {
  if (httpStatus === 413) return 'upload.fileTooLarge';
  switch (reason) {
    case 'offline':
      return 'uploadStatus.offline';
    case 'network':
    case 'timeout':
      return 'uploadStatus.networkError';
    default:
      return 'upload.uploadError';
  }
};

export const uploadReceipt = async (
  uuid: string,
  transaction: Transaction | null | undefined,
  file: File,
  options: ReceiptUploadOptions = {},
): Promise<ReceiptUploadResult> => {
  const handlers = options;
  const basePayload = buildBasePayload(uuid, transaction);
  const filePayload = describeFile(file);
  // Present only on the combined call; absence means a bare upload. Readers (insight, the analysis
  // of a ticket) tell the two apart by it, and a cached old bundle on the same screen has none.
  const confirms = options.confirmPayment ? ({ confirmsPayment: true } as const) : {};
  const endpoint = options.confirmPayment ? '/api/confirm-with-receipt' : '/api/upload';

  for (let attempt = 1; ; attempt += 1) {
    const connection = describeConnection();
    const startedAt = Date.now();
    let progressPercent = 0;
    // Each attempt starts from zero on screen too. Without this the bar keeps the value of the
    // attempt that just died — 83% frozen through the backoff, then jumping back to 4%.
    handlers.onProgress?.(0);

    track({
      name: 'receipt.upload_started',
      payload: { ...basePayload, ...filePayload, ...connection, ...confirms, attempt },
    });

    try {
      const formData = new FormData();
      formData.append('uuid', uuid);
      // The filename is passed explicitly: on a browser without the `File` constructor this is a
      // `Blob` carrying a `name`, and `append` would otherwise send it as "blob".
      formData.append('file', file, file.name);
      // The BFF repeats a request the backend never answered, and must not do that for a
      // replacement: there 409 means "refused to replace", so repeating a first attempt that
      // silently succeeded would report a stored correction as a failure. Only this side knows
      // which kind of upload it is.
      if (options.isReplacement) formData.append('isReplacement', 'true');

      const response = await axios.post(endpoint, formData, {
        signal: options.signal,
        onUploadProgress: (event) => {
          if (!event.total) return;
          progressPercent = Math.round((event.loaded * 100) / event.total);
          handlers.onProgress?.(progressPercent);
        },
      });

      if (response.status !== 200) {
        throw Object.assign(new Error(`Unexpected upload status ${response.status}`), {
          response,
        });
      }

      track({
        name: 'receipt.uploaded',
        payload: {
          ...basePayload,
          ...confirms,
          fileType: file.type,
          fileSize: file.size,
          durationMs: Date.now() - startedAt,
          attempt,
        },
      });
      return { ok: true, alreadyUploaded: false, attempts: attempt };
    } catch (error: any) {
      const httpStatus: number | null = error?.response?.status ?? null;

      // 409 on a first upload means the backend already holds this ticket's receipt — the aftermath
      // of an attempt that timed out on the BFF while the backend finished writing. The file is
      // stored; showing an error here is what sent paying clients to support.
      //
      // On a replacement the same status means the opposite (see `isReplacement`): nothing was
      // stored, so it must surface as a failure.
      if (httpStatus === 409 && !options.isReplacement && !options.confirmPayment) {
        track({
          name: 'receipt.already_uploaded',
          payload: { ...basePayload, attempt },
        });
        return { ok: true, alreadyUploaded: true, attempts: attempt };
      }

      const reason = classifyUploadError(error);
      const durationMs = Date.now() - startedAt;
      const willRetry =
        RETRYABLE.includes(reason) &&
        attempt <= RETRY_DELAYS_MS.length &&
        options.signal?.aborted !== true;

      track({
        name: 'receipt.upload_failed',
        payload: {
          ...basePayload,
          ...filePayload,
          ...connection,
          ...confirms,
          reason,
          httpStatus,
          durationMs,
          attempt,
          progressPercent,
          willRetry,
        },
      });
      beacon(`receipt-fail-${reason}`);
      captureFailure(
        basePayload,
        filePayload,
        connection,
        reason,
        httpStatus,
        durationMs,
        attempt,
        progressPercent,
        error,
      );

      if (!willRetry) {
        return { ok: false, reason, httpStatus, attempts: attempt };
      }

      const delayMs = RETRY_DELAYS_MS[attempt - 1];
      handlers.onRetryScheduled?.(attempt + 1, delayMs);
      await wait(delayMs, reason === 'offline', options.signal);

      // The wait can end because the caller aborted rather than because the delay elapsed.
      if (options.signal?.aborted) {
        return { ok: false, reason, httpStatus, attempts: attempt };
      }
    }
  }
};

/**
 * The page's one action «Я оплатил» with a mandatory receipt: the receipt is stored and the payment
 * confirmed by one request. Same classification, retries and telemetry as `uploadReceipt`.
 */
export const confirmWithReceipt = (
  uuid: string,
  transaction: Transaction | null | undefined,
  file: File,
  options: ReceiptUploadHandlers & { signal?: AbortSignal } = {},
): Promise<ReceiptUploadResult> =>
  uploadReceipt(uuid, transaction, file, { ...options, confirmPayment: true });

/**
 * Text for a failed `confirmWithReceipt`. A request that got no answer, or a server error, has an
 * unknown outcome — the backend may have stored and confirmed before the answer was lost — so the
 * text does not claim anything was or was not saved: it says pressing again is safe, which it is,
 * because the route is idempotent. A 422 is a definite refusal: the ticket no longer awaits payment.
 */
export const confirmErrorTextKey = (
  reason: ReceiptUploadFailureReason,
  httpStatus: number | null,
): string => {
  if (httpStatus === 422) return 'gate.confirmNotAwaiting';
  if (httpStatus === 409 || reason === 'http_5xx' || reason === 'network' || reason === 'timeout') {
    return 'gate.confirmFailedRetry';
  }
  return uploadErrorTextKey(reason, httpStatus);
};
